import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Smartphone, Code, Database, Globe, Zap } from 'lucide-react';
import './Services.css';

const Services = () => {
  const { language } = useLanguage();

  const services = [
    {
      icon: <Smartphone size={22} />,
      title: language === 'fr' ? "Développement Mobile" : "Mobile Development",
      shortDescription: language === 'fr' 
        ? "Spécialisé en Java et Spring Boot, je développe mes compétences en développement mobile."
        : "Proficient in Java and Spring Boot, I am dedicated to expanding my mobile development skills.",
      fullDescription: language === 'fr'
        ? "Expert en développement backend avec Java Spring Boot, je me spécialise dans la création d'APIs robustes et sécurisées. Mon expérience inclut la conception d'architectures microservices, l'optimisation des performances et la mise en place de systèmes de sécurité avancés. Je travaille actuellement sur l'expansion de mes compétences en développement mobile pour offrir des solutions complètes cross-platform."
        : "Expert in backend development with Java Spring Boot, I specialize in creating robust and secure APIs. My experience includes designing microservice architectures, performance optimization, and implementing advanced security systems. I am currently working on expanding my mobile development skills to offer complete cross-platform solutions.",
      color: "#ff6b6b",
      technologies: language === 'fr' 
        ? ["Java", "Spring Boot", "REST APIs", "Microservices", "Sécurité"]
        : ["Java", "Spring Boot", "REST APIs", "Microservices", "Security"]
    },
    {
      icon: <Code size={22} />,
      title: language === 'fr' ? "Intégration d'APIs" : "API Integration",
      shortDescription: language === 'fr'
        ? "Expérimenté dans la conception et l'intégration d'APIs, notamment lors de mon stage chez Geny Shop."
        : "Experienced in designing and integrating APIs, especially during my internship with Geny Shop.",
      fullDescription: language === 'fr'
        ? "Spécialiste de l'intégration d'APIs avec une expérience approfondie dans la conception de systèmes distribués. J'ai développé des APIs RESTful performantes avec documentation Swagger, implémenté des systèmes d'authentification JWT, et créé des pipelines CI/CD. Mon stage chez Geny Shop m'a permis de maîtriser l'intégration de services tiers et l'optimisation des performances API."
        : "API integration specialist with deep experience in designing distributed systems. I have developed high-performance RESTful APIs with Swagger documentation, implemented JWT authentication systems, and created CI/CD pipelines. My internship at Geny Shop allowed me to master third-party service integration and API performance optimization.",
      color: "#4ecdc4",
      technologies: language === 'fr'
        ? ["REST APIs", "Swagger", "JWT", "CI/CD", "Performance"]
        : ["REST APIs", "Swagger", "JWT", "CI/CD", "Performance"]
    },
    {
      icon: <Database size={22} />,
      title: language === 'fr' ? "Développement Full Stack" : "Full Stack Development",
      shortDescription: language === 'fr'
        ? "Avec une solide base en développement backend et frontend."
        : "With a strong foundation in both backend and frontend development.",
      fullDescription: language === 'fr'
        ? "Développeur Full Stack polyvalent maîtrisant l'ensemble de la stack technique moderne. Côté backend : Java Spring Boot, bases de données relationnelles et NoSQL, microservices. Côté frontend : React, Angular, interfaces responsives et PWA. J'utilise des méthodologies Agile et des outils DevOps pour assurer la qualité et la livraison continue."
        : "Versatile Full Stack developer mastering the entire modern technical stack. Backend: Java Spring Boot, relational and NoSQL databases, microservices. Frontend: React, Angular, responsive interfaces and PWA. I use Agile methodologies and DevOps tools to ensure quality and continuous delivery.",
      color: "#45b7d1",
      technologies: language === 'fr'
        ? ["Spring Boot", "React", "Angular", "MySQL", "DevOps"]
        : ["Spring Boot", "React", "Angular", "MySQL", "DevOps"]
    },
    {
      icon: <Globe size={22} />,
      title: language === 'fr' ? "Développement Web" : "Web Development",
      shortDescription: language === 'fr'
        ? "Création de sites web modernes et responsifs avec les dernières technologies."
        : "Creating modern and responsive websites with the latest technologies.",
      fullDescription: language === 'fr'
        ? "Spécialiste du développement web moderne avec expertise en React, Angular et technologies frontend avancées. Je crée des interfaces utilisateur intuitives, des applications web progressives (PWA) et des sites e-commerce performants. Mon approche inclut l'optimisation SEO, l'accessibilité et la performance pour offrir une expérience utilisateur exceptionnelle."
        : "Modern web development specialist with expertise in React, Angular and advanced frontend technologies. I create intuitive user interfaces, progressive web applications (PWA) and high-performance e-commerce sites. My approach includes SEO optimization, accessibility and performance to deliver an exceptional user experience.",
      color: "#f39c12",
      technologies: language === 'fr'
        ? ["React", "Angular", "HTML5", "CSS3", "JavaScript"]
        : ["React", "Angular", "HTML5", "CSS3", "JavaScript"]
    },
    {
      icon: <Zap size={22} />,
      title: language === 'fr' ? "Optimisation Performance" : "Performance Optimization",
      shortDescription: language === 'fr'
        ? "Amélioration des performances et de la scalabilité des applications."
        : "Improving application performance and scalability.",
      fullDescription: language === 'fr'
        ? "Spécialiste de l'optimisation des performances avec une approche data-driven. J'analyse et optimise les bases de données, les requêtes API et les interfaces utilisateur pour maximiser la vitesse et l'efficacité. Mes techniques incluent la mise en cache, la compression des données, l'optimisation des images et la réduction du temps de chargement."
        : "Performance optimization specialist with a data-driven approach. I analyze and optimize databases, API queries and user interfaces to maximize speed and efficiency. My techniques include caching, data compression, image optimization and loading time reduction.",
      color: "#9b59b6",
      technologies: language === 'fr'
        ? ["MySQL", "Cache", "Compression", "Optimisation", "Monitoring"]
        : ["MySQL", "Cache", "Compression", "Optimization", "Monitoring"]
    }
  ];

  return (
    <section className="services" id="services">
      <div className="services-container">
        <div className="section-intro">
          <h2 className="heading">
            {language === 'fr' ? <>Mes <span>Services</span></> : <>My <span>Services</span></>}
          </h2>
          <p className="section-subtitle">
            {language === 'fr'
              ? 'Domaines d\'intervention technique et produit.'
              : 'Technical and product areas of expertise.'}
          </p>
        </div>
        
        <div className="services-grid">
          {services.map((service, index) => (
            <div key={index} className="service-box">
              <div className="service-icon">
                {service.icon}
              </div>
              <h3>{service.title}</h3>
              <p>{service.shortDescription}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
